import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { getMyRewards, claimReward } from "@/lib/api";
import { toast } from "sonner";

function statusTone(ui) {
  const s = (ui || "").toLowerCase();
  if (s.includes("settled")) return "text-positive";
  if (s.includes("fail")) return "text-negative";
  if (s.includes("claimable") || s.includes("eligible")) return "text-gold";
  return "text-zinc-500";
}

export default function RewardsPage() {
  const [data, setData] = useState({ rewards: [], settlement_enabled: false, disclaimer: "" });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await getMyRewards();
      setData(res);
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 401
        ? "Connect your wallet session to view rewards."
        : (err?.response?.data?.detail || "Could not load rewards"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onClaim = async (id) => {
    setClaiming(id);
    try {
      const res = await claimReward(id);
      if (res?.gates?.broadcast_allowed === false || res?.settlement?.dry_run) {
        toast.success("Claim requested — settlement is dry-run / disabled");
      } else {
        toast.success(res?.note || "Claim requested");
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div className="min-h-screen grain">
      <Navbar category="tokens" />
      <main className="mx-auto max-w-2xl px-4 py-8">
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600">Account</p>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight">Rewards</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Campaign participation rewards are separate from Verified Backing and never change rank. Merkle claim campaigns may require a network fee to claim onchain.
        </p>
        {data.disclaimer && (
          <p className="mt-3 text-xs text-zinc-600">{data.disclaimer}</p>
        )}
        {(!data.settlement_enabled || data.dry_run || !data.broadcast_allowed) && (
          <p className="mt-2 text-xs text-amber-500/80" data-testid="settlement-disabled-banner">
            Production settlement is disabled. Claims update accounting only — not payment completed.
          </p>
        )}

        {loading && <div className="mt-8 text-sm text-zinc-500">Loading…</div>}
        {error && <div className="mt-8 text-sm text-zinc-400" data-testid="rewards-error">{error}</div>}

        <div className="mt-8 space-y-2" data-testid="rewards-list">
          {(data.rewards || []).map((r) => {
            const decimals = r.decimals || 6;
            const amount = (r.allocation_amount_base_units || 0) / (10 ** decimals);
            const ui = r.ui_status || r.status;
            const complete = r.settlement_complete === true;
            return (
              <div
                key={r.id}
                className="rounded-xl border border-white/10 px-4 py-3"
                data-testid={`reward-${r.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className={`text-[10px] uppercase tracking-wider ${statusTone(ui)}`} data-testid={`reward-status-${r.id}`}>
                      {ui}
                    </div>
                    <div className="mt-1 font-display text-lg font-black">
                      {amount.toLocaleString(undefined, { maximumFractionDigits: 4 })} {r.asset_symbol || "TOKEN"}
                    </div>
                    <div className="mt-1 text-xs text-zinc-500">
                      Eligible Backing {r.eligible_backing_usd != null ? `$${Number(r.eligible_backing_usd).toLocaleString()}` : "—"}
                    </div>
                    {complete && r.explorer_url && (
                      <a
                        href={r.explorer_url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-2 inline-block text-xs text-gold hover:underline"
                        data-testid={`reward-explorer-${r.id}`}
                      >
                        View settlement tx
                      </a>
                    )}
                    {!complete && r.status !== "claimable" && r.status !== "calculated" && (
                      <p className="mt-2 text-[11px] text-zinc-600">
                        Not marked complete until Settled.
                      </p>
                    )}
                  </div>
                  {r.status === "claimable" && (
                    <button
                      type="button"
                      disabled={claiming === r.id}
                      onClick={() => onClaim(r.id)}
                      className="rounded-full border border-white/15 px-3 py-1.5 text-xs uppercase tracking-wider hover:text-gold"
                      data-testid={`reward-claim-${r.id}`}
                    >
                      {claiming === r.id ? "…" : "Claim"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          {!loading && !error && !(data.rewards || []).length && (
            <div className="rounded-xl border border-white/10 px-4 py-8 text-sm text-zinc-500">
              No reward allocations yet.
            </div>
          )}
        </div>
        <Link to="/battles" className="mt-8 inline-block text-xs text-zinc-500 hover:text-white">← Battles</Link>
      </main>
    </div>
  );
}
